import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { List, Set } from 'immutable';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { DialogActionsEnum, IDialogTrigger } from '../../../dialog/dialog.data';
import { DialogService } from '../../../dialog/dialog.service';
import {
  SEARCH_LIST_FILTER,
  SortByEnum,
  SortOrderEnum,
} from '../../../entity-data/entity-data.data';
import { Expense } from '../../../expense-data/models/expense.model';
import { INoItemsOptions } from '../../../feed/feed.interface';
import { SearchActionTriggersEnum } from '../../../search/search.component';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { TrackingAdditionalPropertiesBuilder } from '../../../tracking/tracking-additional-properties.builder';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import {
  IExpensesMultipleSelectAdditionalProperties,
  ITrackingAction,
  ITrackingObject,
  TrackingDialogNamesEnum,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { ExpenseDataDispatcher } from '../../../expense-data/expense-data.dispatcher';
import { ExpenseDataStore } from '../../../expense-data/expense-data.store';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';

export const DIALOG_TYPE = 'expense-picker';

@Component({
  selector: 'app-expense-picker-dialog',
  templateUrl: './expense-picker-dialog.component.html',
  styleUrls: ['./expense-picker-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExpensePickerDialogComponent implements OnInit, OnDestroy {
  @Input() attachmentLimitErrorMessage: string;
  @Input() maxExpensesAllowed: number;
  @Input() expensesAdded: number;
  @Input() maxAttachmentsAllowed: number;
  @Input() attachmentsAdded: number;
  @Input() trackingScreenName: TrackingScreenNamesEnum;

  @Output() onSelectExpenses: EventEmitter<List<Expense>> = new EventEmitter<
    List<Expense>
  >();

  private _inSearchMode: boolean;
  private _searchActionTriggers: Subject<SearchActionTriggersEnum>;
  private _selectedExpenses: Set<Expense>;
  private _bulkActionControls: { [itemId: string]: FormControl } = {};
  private _destroyed: Subject<boolean> = new Subject<boolean>();

  dialogType: string;
  noExpensesOptions: INoItemsOptions;
  attachmentIcon: IIcon;
  trackingObject: ITrackingObject;
  addBulkAction: ITrackingAction;

  error$: BehaviorSubject<string>;
  dialogButtonText$: BehaviorSubject<string>;
  expensesMultipleSelectTrackingProperties$: BehaviorSubject<IExpensesMultipleSelectAdditionalProperties>;
  filteredExpenses$: Observable<List<Expense>>;
  numberOfSelectedExpenses$: Observable<number>;
  searchActionTriggers$: Observable<SearchActionTriggersEnum>;

  constructor(
    private _translateService: TranslateService,
    private _dialogService: DialogService,
    private _expenseDataDispatcher: ExpenseDataDispatcher,
    private _expenseDataStore: ExpenseDataStore,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _trackingAdditionalPropertiesBuilder: TrackingAdditionalPropertiesBuilder
  ) {}

  ngOnInit() {
    this._searchActionTriggers = new Subject<SearchActionTriggersEnum>();

    this.trackingObject = this._trackingObjectBuilder.buildDialog(
      TrackingDialogNamesEnum.expenses_multiple_select
    );
    this.addBulkAction = this._trackingActionBuilder.buildButtonTapped(
      'add_bulk',
      this.trackingScreenName
    );
    this.searchActionTriggers$ = this._searchActionTriggers.asObservable();
    this.dialogType = DIALOG_TYPE;
    this.noExpensesOptions = {
      description: this._translateService.instant('LABEL.NO_EXPENSES'),
    };
    this.attachmentIcon = {
      name: 'add-photo',
      size: IconSizesEnum.medium,
    };

    this.error$ = new BehaviorSubject<string>(null);
    this.dialogButtonText$ = new BehaviorSubject<string>(
      this._translateService.instant('ACTION.ADD_EXPENSE')
    );
    this.expensesMultipleSelectTrackingProperties$ = new BehaviorSubject<IExpensesMultipleSelectAdditionalProperties>(
      null
    );

    // feed results
    this.filteredExpenses$ = this._expenseDataStore.getSortedList(
      SEARCH_LIST_FILTER
    );

    // update number of selected expenses
    this.numberOfSelectedExpenses$ = this._expenseDataStore
      .getSelectedEntities()
      .pipe(
        tap((expenses: Set<Expense>) => {
          this._selectedExpenses = expenses || Set([]);
        }),
        map((expenses: Set<Expense>) => (expenses && expenses.size) || 0)
      );

    // on open dialog
    this._dialogService.dialogTriggers$
      .pipe(
        filter((dialogTrigger: IDialogTrigger) => {
          return (
            dialogTrigger.type === this.dialogType &&
            dialogTrigger.action === DialogActionsEnum.open
          );
        }),
        takeUntil(this._destroyed)
      )
      .subscribe(() => {
        this._inSearchMode = false;

        this.resetSelectedExpenses();

        this._expenseDataStore
          .getListFetched(SEARCH_LIST_FILTER)
          .pipe(
            takeUntil(this._destroyed),
            take(1),
            filter((fetched: boolean) => !fetched)
          )
          .subscribe((fetched: boolean) => this.searchExpenses());
      });
  }

  onSearch(searchTerm: string) {
    this.resetSelectedExpenses();

    if (searchTerm) {
      this._inSearchMode = true;

      this.searchExpenses(searchTerm.toLowerCase());
    } else {
      this._inSearchMode = false;

      this.searchExpenses();
    }
  }

  searchExpenses(query?: string) {
    this._expenseDataDispatcher.searchList(
      query,
      query ? SortByEnum.date : SortByEnum.created_date,
      SortOrderEnum.desc,
      false,
      false,
      false
    );
  }

  onClickExpense(expense: Expense) {
    const currentValue = this._bulkActionControls[expense.id].value;

    this._bulkActionControls[expense.id].setValue(!currentValue, {
      emitEvent: true,
    });
  }

  getBulkActionControl(expense: Expense): FormControl {
    const expenseId = expense.id;
    const isSelected = this._selectedExpenses.find(
      (selectedExpense: Expense) => selectedExpense.id === expenseId
    );

    if (!this._bulkActionControls) {
      this._bulkActionControls = {};
    }

    if (!this._bulkActionControls[expenseId]) {
      this._bulkActionControls[expenseId] = new FormControl(isSelected);

      this._bulkActionControls[expenseId].valueChanges
        .pipe(takeUntil(this._destroyed))
        .subscribe((value: boolean) => this.onBulkActionSelect(expense, value));
    } else {
      this._bulkActionControls[expenseId].setValue(isSelected, {
        emitEvent: false,
      });
    }

    return this._bulkActionControls[expenseId];
  }

  onBulkActionSelect(expense: Expense, selected: boolean) {
    const belowDocumentItemLimit =
      this._selectedExpenses.size <
      this.maxExpensesAllowed - this.expensesAdded;

    this.error$.next(null);

    if (selected) {
      if (belowDocumentItemLimit) {
        this._expenseDataDispatcher.addToSelectedList(expense);
      } else {
        this.error$.next(
          this._translateService.instant('WARNING.BULK_SELECT_LIMIT', {
            maxNumber: this.maxExpensesAllowed,
          })
        );

        this._expenseDataDispatcher.removeFromSelectedList(expense);

        this._bulkActionControls[expense.id].setValue(false, {
          emitEvent: false,
        });
      }
    } else {
      this._expenseDataDispatcher.removeFromSelectedList(expense);
    }

    const numberOfSelectedExpenses = this._selectedExpenses.size;
    const numberOfSelectedExpensesWithAttachment = this._selectedExpenses.filter(
      (expense: Expense) => !!expense.file
    );
    const belowAttachmentLimit =
      numberOfSelectedExpensesWithAttachment.size <=
      this.maxAttachmentsAllowed - this.attachmentsAdded;

    if (belowAttachmentLimit) {
      if (numberOfSelectedExpenses <= 1) {
        this.dialogButtonText$.next(
          this._translateService.instant('ACTION.ADD_EXPENSE')
        );
      } else {
        this.dialogButtonText$.next(
          this._translateService.instant('ACTION.ADD_N_EXPENSES', {
            count: numberOfSelectedExpenses,
          })
        );
      }
    } else {
      this.error$.next(this.attachmentLimitErrorMessage);

      if (numberOfSelectedExpenses <= 1) {
        this.dialogButtonText$.next(
          this._translateService.instant('ACTION.ADD_WITHOUT_IMAGES')
        );
      } else {
        this.dialogButtonText$.next(
          this._translateService.instant('ACTION.ADD_WITHOUT_N_IMAGES', {
            attachmentCount: numberOfSelectedExpenses,
          })
        );
      }
    }

    // update tracking addtional properties
    this.expensesMultipleSelectTrackingProperties$.next(
      this._trackingAdditionalPropertiesBuilder.buildExpenseMultipleSelectOptions(
        numberOfSelectedExpenses,
        !belowAttachmentLimit
      )
    );
  }

  saveSelectedExpenses() {
    this.onSelectExpenses.emit(this._selectedExpenses.toList());

    this.resetSelectedExpenses();
  }

  resetSelectedExpenses() {
    this.error$.next(null);

    this.dialogButtonText$.next(
      this._translateService.instant('ACTION.ADD_EXPENSE')
    );

    this._expenseDataDispatcher.resetSelectedList();
  }

  onClose() {
    if (this._inSearchMode) {
      this._searchActionTriggers.next(SearchActionTriggersEnum.reset);
    }
  }

  groupByFn = (expense: Expense) =>
    expense.category || this._translateService.instant('LABEL.NO_CATEGORY');

  sortGroupsByFn = (_, category: string) =>
    category === this._translateService.instant('LABEL.NO_CATEGORY')
      ? ''
      : category;

  trackByFn = (index: number, expense: Expense) => expense.id;

  ngOnDestroy() {
    this._expenseDataDispatcher.clearList(SEARCH_LIST_FILTER);

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
